import {
  LmnpLmpDto,
  UnfurnishedDto,
} from '@app/services/funnel/estate/funnel-bien-locatif.service';
import {
  EstateTypeEnum,
  PersonalUseTypeEnum,
} from '@app/services/funnel/estate/funnel-estate-shared.types';
import {
  OccupancyStatusEnum,
  PersonalUsePropertyDto,
} from '@app/services/funnel/estate/funnel-residence-principale.service';
import { LoanDto } from '@app/services/funnel/funnel-loan.service';
import { OwnerDto } from '@app/services/funnel/funnel-ownership.service';
import { ScpiDto } from '@app/services/scpi.service';

/**
 * Types et interfaces partagés pour le formulaire de biens immobiliers
 */
export enum RentalType {
  FURNISHED = 'FURNISHED',
  UNFURNISHED = 'UNFURNISHED',
}

// TypeUrlEnum: Pour les URLs de type de bien (backend)
export enum TypeUrlEnum {
  PERSONAL_USE = 'personalUse', // /user/{userId}/realestate/personalUse
  UNFURNISHED = 'unfurnished', // /user/{userId}/realestate/unfurnished
  LMNPLMNP = 'lmplmnp', // /user/{userId}/realestate/lmplmnp
  SCPI = 'scpi', // /user/{userId}/realestate/scpi
}

/**
 * Champs UI communs à tous les types de biens
 */
interface EstateUIBase {
  type: string; // Label d'affichage (ex: "Résidence principale")
  typeValue: EstateTypeEnum; // Type enum pour le routing
  owners?: OwnerDto[]; // Détenteurs + part de détention (`share`, fraction 0..1)
  startDate?: string | Date; // Date d'achat
  loan?: LoanDto; // Crédit associé (si paymentType = CREDIT)
}

/**
 * Champs UI spécifiques aux biens avec dépenses mensuelles (pour l'UI)
 */
interface EstateWithExpensesUI {
  monthlyRent?: number; // Loyer mensuel
  propertyTax?: number; // Taxe foncière annuelle
  coOwnershipFees?: number; // Charges de copropriété annuelles
  managementFees?: number; // Frais de gestion annuels
  insuranceFees?: number; // Assurance annuelle
  housingTax?: number;
  cfe?: number;
  accountingFees?: number;
}

/**
 * Résidence Principale UI
 * Combine PersonalUsePropertyDto avec les champs UI nécessaires
 */
export type ResidencePrincipaleUI = PersonalUsePropertyDto &
  EstateUIBase &
  EstateWithExpensesUI & {
    typeValue: EstateTypeEnum.RESIDENCE_PRINCIPALE;
    situation: OccupancyStatusEnum;
    originalSituation?: OccupancyStatusEnum;
    personalUseType: PersonalUseTypeEnum.RESIDENCE_PRINCIP; // Toujours RESIDENCE_PRINCIP
  };

/**
 * Bien de Jouissance UI
 * (Résidence secondaire, garage, terrain, bois, surface)
 */
export type BienJouissanceUI = PersonalUsePropertyDto &
  EstateUIBase &
  EstateWithExpensesUI & {
    typeValue: EstateTypeEnum.ENJOYMENT;
    personalUseType: PersonalUseTypeEnum;
  };

/**
 * Bien Locatif UI
 * Union de UnfurnishedDto et LmnpLmpDto avec champs UI
 */
export type BienLocatifUI = (UnfurnishedDto | LmnpLmpDto) &
  EstateUIBase &
  EstateWithExpensesUI & {
    typeValue: EstateTypeEnum.RENTAL;
    rentalType: RentalType; // FURNISHED ou UNFURNISHED
    originalRentalType?: RentalType; // Type original (pour détecter les changements)
  };

/**
 * SCPI UI
 */
export type ScpiUI = ScpiDto &
  EstateUIBase & {
    typeValue: EstateTypeEnum.SCPI;
    scpi?: number | { id: number; name: string; [key: string]: any }; // ID ou objet SCPI
  };

/**
 * Union type principal pour tous les biens immobiliers dans l'UI
 */
export type EstateUI =
  | ResidencePrincipaleUI
  | BienJouissanceUI
  | BienLocatifUI
  | ScpiUI;

export enum ESTATE_FIELDNAME {
  SITUATION = 'situation',
  TYPE = 'type',
  RENTAL_TYPE = 'rentalType',
  PERSONAL_USE_TYPE = 'personalUseType',
  LABEL = 'label',
  PURCHASE_PRICE = 'purchasePrice',
  START_DATE = 'startDate',
  AREA_SIZE = 'areaSize',
  ADDRESS = 'address',
  // Détenteurs + part de détention (`owners: OwnerShare[]`, champ OWNER_SHARE).
  OWNER_ID = 'owners',
  OWNERSHIP_TYPE = 'ownershipType',
  SUBSCRIPTION_SOURCE = 'subscriptionSource',
  MONTHLY_RENT = 'monthlyRent',
  PROPERTY_TAX = 'propertyTax',
  CO_OWNERSHIP_FEES = 'coOwnershipFees',
  MANAGEMENT_FEES = 'managementFees',
  INSURANCE_FEES = 'insuranceFees',
  HOUSING_TAX = 'housingTax',
  CFE = 'cfe',
  ACCOUNTING_FEES = 'accountingFees',
  SCPI = 'scpi',
  NB_SHARE = 'nbShare',
  INITIAL_PART_PRICE = 'initialPartPrice',
  FISCAL_LAW_TYPE = 'fiscalLawType',
  DISMEMBER_DURATION = 'dismemberDuration',
  DISMEMBER_KEY = 'dismemberKey',
}

/**
 * Types helpers pour les références
 */
export interface OwnerOption {
  id: number;
  value: string;
}

export interface Country {
  id: string;
  value: string;
  code?: string;
}

export interface SubscriptionSource {
  id: string;
  value: string;
}

export interface User {
  id: number;
  accountId: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ScpiProduct {
  id: number;
  name: string;
  isin?: string;
  manager?: string;
}

export interface EstateTypeOption {
  id: string;
  label: string;
}

export const ESTATE_TYPES: EstateTypeOption[] = [
  { id: EstateTypeEnum.RESIDENCE_PRINCIPALE, label: 'Résidence principale' },
  { id: EstateTypeEnum.ENJOYMENT, label: 'Bien de jouissance' },
  { id: EstateTypeEnum.RENTAL, label: 'Bien locatif' },
  { id: EstateTypeEnum.SCPI, label: 'SCPI' },
];

export enum LOAN_FIELDNAME_ESTATE {
  PAYMENT_TYPE = 'paymentType',
  START_TYPE = 'startType',
  AMOUNT_BORROWED = 'amountBorrowed',
  START_DATE = 'loanStartDate', // Pour éviter le conflit avec Estate.startDate
  REMAINING_AMOUNT = 'remainingAmount',
  DURATION_TOTAL_IN_MONTH = 'durationTotalInMonth',
  INTEREST_RATE = 'interestRate',
  INSURANCE_RATE = 'insuranceRate',
  // Détenteurs + part de détention du crédit du bien (`owners: OwnerShare[]`,
  // champ OWNER_SHARE). Nom distinct de l'Estate pour éviter toute collision.
  OWNER_ID = 'loanOwners',
}
